import React, { useEffect, useState } from "react";
import { Feedback, BugReport, Mms } from "@mui/icons-material";
import {
  Button,
  CircularProgress,
  Typography,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { Link, useParams } from "react-router-dom";
import { UserContext } from "src/components/userContext";

type IngameFeedbackEntry = {
  id: number;
  projectSlug: string;
  tableId?: string;
  username?: string;
  responseType: "game-feedback" | "bug-report" | "cgo-feedback" | string;
  responseValue: string;
  eventNumber?: number;
  createdAt: number;
};

export default function IngameFeedbackResults() {
  const [items, setItems] = useState<IngameFeedbackEntry[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<"all" | "game-feedback" | "bug-report" | "cgo-feedback">("all");

  const params = useParams();
  const projectSlug = params.projectSlug as string;
  const userContext = React.useContext(UserContext);

  useEffect(() => {
    document.title = "Ingame Feedback - " + projectSlug;
    if (!userContext.hasPermission("see-ingame-feedback-results")) return;
    setLoading(true);
    setError(null);
    fetch("/api/ingame-feedback/" + projectSlug)
      .then((r) => {
        if (!r.ok) throw new Error("Failed to load feedbacks");
        return r.json();
      })
      .then((data: IngameFeedbackEntry[]) => {
        const normalized = (data || [])
          .map((d) => ({ ...d, createdAt: Number(d.createdAt) }))
          .sort((a, b) => b.createdAt - a.createdAt);
        setItems(normalized);
      })
      .catch((e) => {
        setError(String(e.message || e));
        setItems([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [projectSlug, userContext]);

  function visibleItems(): IngameFeedbackEntry[] {
    if (!items) return [];
    return items.filter((it) => {
      if (filterType === "all") return true;
      return it.responseType === filterType;
    });
  }

  function iconForType(t: string) {
    if (t === "bug-report") return <BugReport fontSize="small" sx={{ mr: 1 }} />;
    if (t === "cgo-feedback") return <Mms fontSize="small" sx={{ mr: 1 }} />;
    return <Feedback fontSize="small" sx={{ mr: 1 }} />;
  }

  if (!userContext.hasPermission("see-ingame-feedback-results")) {
    return (
      <Typography color="error" sx={{ p: 1 }}>
        You should not be here...
      </Typography>
    );
  }

  return (
    <div style={{ width: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <Button variant={filterType === "all" ? "contained" : "outlined"} onClick={() => setFilterType("all")}>
          All
        </Button>
        <Button
          startIcon={<Feedback />}
          variant={filterType === "game-feedback" ? "contained" : "outlined"}
          onClick={() => setFilterType("game-feedback")}
        >
          Game
        </Button>
        <Button
          startIcon={<BugReport />}
          variant={filterType === "bug-report" ? "contained" : "outlined"}
          onClick={() => setFilterType("bug-report")}
        >
          Bugs
        </Button>
        <Button
          startIcon={<Mms />}
          variant={filterType === "cgo-feedback" ? "contained" : "outlined"}
          onClick={() => setFilterType("cgo-feedback")}
        >
          CGO
        </Button>
        <Chip label={items ? `${visibleItems().length} shown` : ""} />
      </div>

      {loading && (
        <div style={{ display: "flex", justifyContent: "center", padding: 16 }}>
          <CircularProgress />
        </div>
      )}

      {!loading && error && (
        <Typography color="error" sx={{ p: 1 }}>
          {error}
        </Typography>
      )}

      {!loading && !error && (!items || items.length === 0) && <Typography sx={{ p: 1 }}>No feedback yet.</Typography>}

      {!loading && !error && items && items.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small" aria-label="feedback table">
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Table</TableCell>
                <TableCell>Message</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleItems().map((it) => (
                <TableRow key={it.id} hover>
                  <TableCell>{iconForType(it.responseType)}</TableCell>
                  <TableCell>
                    <Typography variant="subtitle2">{it.username || "unknown"}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {new Date(it.createdAt).toLocaleString("cs-CZ")}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {it.tableId ? <Link to={`./../table/${it.tableId}`}>{it.tableId}</Link> : "-"}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "pre-wrap" }}>
                    <Typography>{it.responseValue}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </div>
  );
}
